import React, { memo, useEffect, useMemo, useRef } from 'react';
import {
	Control,
	FieldErrors,
	FieldValues,
	UseFormGetValues,
	UseFormRegister,
	UseFormSetValue,
	UseFormTrigger,
	UseFormWatch,
} from 'react-hook-form';

import {
	Button,
	Datepicker,
	GridContainer,
	Input,
	InputType,
	Select,
	TextArea,
} from '@components/common';
import { THandleChange } from '@components/common/Select/Select';

import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { getIsRequiredField } from '@utils/assetForm';

import {
	IAssetSubtypeById,
	IAssetTypeSubtypeResponse,
} from '@interfaces/assets/asset';
import {
	IAssetFormValues,
	IContactPersons,
	TResponsible,
} from '@interfaces/components/form';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { regExpDecimal, regExpWithoutSpace } from '@constants/regExp';
import { basicInformationAreas, fieldsToTestBasicInformation, tabsNames } from '../constants';

import styles from './BasicInformation.module.scss';

export interface IBasicInformationProps {
	setCurrentTab: React.Dispatch<React.SetStateAction<string>>;
	setSelectedAssetType: React.Dispatch<React.SetStateAction<string | null>>;
	setRequiredFields: React.Dispatch<React.SetStateAction<string | null>>;
	initialState: IAssetFormValues;
	saveValues: (values: IAssetFormValues) => void;
	isEditMode: boolean;
	assetTypes: Array<TSelectOptionType<string> & IAssetTypeSubtypeResponse>;
	assetSubTypesByType: Array<IAssetSubtypeById & TSelectOptionType<string>>;
	responsibleOptions: Array<TResponsible>;
	contactPersonsOptions: Array<IContactPersons>;
	checkUniqueAssetId: (params: {
		assetId?: string;
		id?: string | null;
		successHandler: () => void;
		errorHandler?: React.Dispatch<React.SetStateAction<string | undefined>>;
	}) => void;
	setAssetCheckError: React.Dispatch<React.SetStateAction<string | undefined>>;
	assetCheckError: any;
	register: UseFormRegister<FieldValues>;
	errors: FieldErrors;
	control: Control;
	setValue: UseFormSetValue<FieldValues>;
	watch: UseFormWatch<FieldValues>;
	trigger: UseFormTrigger<FieldValues>;
	getValues: UseFormGetValues<FieldValues>;
	requiredFields?: string[] | null;
}

const BasicInformation = ({
	setCurrentTab,
	initialState,
	saveValues,
	isEditMode,
	assetTypes,
	assetSubTypesByType,
	responsibleOptions,
	contactPersonsOptions,
	checkUniqueAssetId,
	setSelectedAssetType,
	setRequiredFields,
	setAssetCheckError,
	assetCheckError,
	register,
	errors,
	control,
	setValue,
	watch,
	trigger,
	getValues,
	requiredFields,
}: IBasicInformationProps) => {
	const basicInfoRef: React.Ref<HTMLDivElement> = useRef(null);

	const subType = watch('subType');
	const lastVerificationDate = watch('lastVerificationDate')
		? new Date(watch('lastVerificationDate'))
		: null;
	const siteAvailabilityReviewDate = watch('siteAvailabilityReviewDate')
		? new Date(watch('siteAvailabilityReviewDate'))
		: null;

	const assetType = watch('assetType');
	const isActiveSubtype = useMemo(() => !!assetSubTypesByType.length, [assetSubTypesByType.length]);

	useEffect(() => {
		setRequiredFields(assetType?.type?.requiredSystemFieldNames || null);
		setSelectedAssetType(assetType?.value || null);
	}, [assetType, setRequiredFields, setSelectedAssetType]);

	useEffect(() => {
		if (initialState.subType) {
			setValue('subType', initialState.subType);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialState.subType]);

	useEffect(() => {
		if (initialState.assetType) {
			setValue('assetType', initialState.assetType);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialState.assetType]);

	useEffect(() => {
		if (initialState.responsible) {
			setValue('responsible', initialState.responsible);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialState.responsible]);

	useEffect(() => {
		if (initialState.contactPersonId) {
			setValue('contactPersonId', initialState.contactPersonId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialState.contactPersonId]);

	useOnClickOutside({
		refs: [basicInfoRef],
		handler: () => {
			if (isEditMode) {
				saveValues(getValues());
			}
		},
	});

	const resetAssetIdError = () => setAssetCheckError(undefined);

	const onSubmit = async () => {
		const isValid = await trigger(fieldsToTestBasicInformation);

		if (!isValid) return;

		if (!isEditMode) {
			const switchToNext = () => {
				saveValues(getValues());

				setCurrentTab(tabsNames.address);
			};

			checkUniqueAssetId({
				assetId: watch('assetId'),
				successHandler: switchToNext,
				errorHandler: setAssetCheckError,
			});
		}
	};

	const revalidateLastVerificationDate = async () => {
		await trigger('lastVerificationDate');
	};

	const handleChangeLastDate = async (value: number | null) => {
		await setValue('lastVerificationDate', !value ? null : value);
		await revalidateLastVerificationDate();
	};

	const revalidateSiteAvailabilityReviewDate = async () => {
		await trigger('siteAvailabilityReviewDate');
	};

	const handleChangeReviewDate = async (value: number | null) => {
		await setValue('siteAvailabilityReviewDate', !value ? null : value);
		await revalidateSiteAvailabilityReviewDate();
	};

	const handleChangeAssetType = (option: THandleChange) => {
		setValue('assetType', option);
		setValue('subType', null);
	};

	const isRequiredHeight = getIsRequiredField({ fieldName: 'height', requiredFields });
	const isRequiredUprn = getIsRequiredField({ fieldName: 'uprn', requiredFields });
	const isRequiredSubType = getIsRequiredField({ fieldName: 'subType', requiredFields });
	const isRequiredResponsible = getIsRequiredField({ fieldName: 'responsible', requiredFields });
	const isRequiredSiteAvailabilityReviewDate = getIsRequiredField({
		fieldName: 'siteAvailabilityReviewDate',
		requiredFields,
	});
	const isRequiredLastVerificationDate = getIsRequiredField({ fieldName: 'lastVerificationDate', requiredFields });
	const isRequiredAssetOwnerNotes = getIsRequiredField({ fieldName: 'assetOwnerNotes', requiredFields });

	return (
		<div ref={basicInfoRef}>
			<GridContainer
				columns={6}
				customClassName={styles.basicInformationFormContainer}
				gap='15px 20px'
				gridTemplateAreas={basicInformationAreas}
			>
				<div className={styles.assetId}>
					<Input
						errorMessage={errors?.assetId?.message || assetCheckError}
						label='Asset ID'
						name='assetId'
						onFocus={resetAssetIdError}
						placeholder='Enter asset ID'
						regexValidation={regExpWithoutSpace}
						register={register}
						required
						setValue={setValue}
					/>
				</div>
				<div className={styles.contactPersonId}>
					<Select
						control={control}
						errorMessage={errors.contactPersonId?.message}
						isError={!!errors?.contactPersonId}
						label='Contact Person'
						name='contactPersonId'
						options={contactPersonsOptions}
						placeholder='Select contact person'
					/>
				</div>
				<div className={styles.assetType}>
					<Select
						control={control}
						errorMessage={errors.assetType?.message}
						handleChange={handleChangeAssetType}
						isError={!!errors?.assetType}
						label='Asset Type'
						name='assetType'
						options={assetTypes}
						placeholder='Select type'
						required
					/>
				</div>
				<div className={styles.assetSubType}>
					<Select
						control={control}
						disabled={!isActiveSubtype}
						errorMessage={errors.subType?.message}
						isClearable
						isError={!!errors?.subType}
						label='Asset Subtype'
						name='subType'
						options={assetSubTypesByType}
						placeholder='Select subtype'
						required={isRequiredSubType}
						value={subType}
					/>
				</div>
				<div className={styles.uprn}>
					<Input
						errorMessage={errors?.uprn?.message}
						label='UPRN'
						name='uprn'
						placeholder='Enter UPRN'
						register={register}
						required={isRequiredUprn}
						setValue={setValue}
						type={InputType.integer}
					/>
				</div>
				<div className={styles.responsible}>
					<Select
						control={control}
						errorMessage={errors.responsible?.message}
						hideSelectedOptions
						isClearable
						isError={!!errors?.responsible}
						label='Responsible'
						name='responsible'
						options={responsibleOptions}
						placeholder='Select responsible'
						required={isRequiredResponsible}
					/>
				</div>
				<div className={styles.price}>
					<Input
						errorMessage={errors?.indicativeAcceptablePricing?.message}
						label='Price'
						name='indicativeAcceptablePricing'
						placeholder='Enter price'
						regexValidation={regExpDecimal}
						register={register}
						required
						setValue={setValue}
						type={InputType.price}
					/>
				</div>
				<div className={styles.maxOperators}>
					<Input
						errorMessage={errors?.maxOperators?.message}
						label='Operators'
						name='maxOperators'
						placeholder='Enter count'
						register={register}
						setValue={setValue}
						type={InputType.integer}
					/>
				</div>
				<div className={styles.height}>
					<Input
						errorMessage={errors?.height?.message}
						label='Height'
						name='height'
						placeholder='Enter height'
						register={register}
						required={isRequiredHeight}
						setValue={setValue}
						type={InputType.integer}
					/>
				</div>
				<div className={styles.lastVerificationDate}>
					<Datepicker
						key='lastVerificationDate'
						disableFutureDates
						disableInput
						errorMessage={errors?.lastVerificationDate?.message}
						handleChange={handleChangeLastDate}
						isClearable
						isError={!!errors?.lastVerificationDate}
						isFullWidth
						isMobileDevice={false}
						label='Last Verification Date'
						required={isRequiredLastVerificationDate}
						selected={lastVerificationDate}
					/>
				</div>
				<div className={styles.siteAvailabilityReviewDate}>
					<Datepicker
						key='siteAvailabilityReviewDate'
						disableInput
						errorMessage={errors?.siteAvailabilityReviewDate?.message}
						handleChange={handleChangeReviewDate}
						isClearable
						isError={!!errors?.siteAvailabilityReviewDate}
						isFullWidth
						isMobileDevice={false}
						label='Site Availability Review Date'
						required={isRequiredSiteAvailabilityReviewDate}
						selected={siteAvailabilityReviewDate}
					/>
				</div>
				<div className={styles.assetOwnerNotes}>
					<TextArea
						errorMessage={errors?.assetOwnerNotes?.message}
						label='Asset Notes'
						name='assetOwnerNotes'
						placeholder='Enter asset description'
						register={register}
						required={isRequiredAssetOwnerNotes}
						rows={3}
						withUseForms
					/>
				</div>
				{!isEditMode
					&& <div className={styles.submitButton}>
						<Button
							onClick={onSubmit}
							text='Next'
						/>
					</div>
				}
			</GridContainer>
		</div>
	);
};

export default memo(BasicInformation);
